import Info from '@mui/icons-material/Info';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import Switch from '@mui/material/Switch';
import Tooltip from '@mui/material/Tooltip';
import useTheme from '@mui/material/styles/useTheme';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import Iconify from '../../components/iconify/component';
import { type IconifyProps } from '../../components/iconify/types';
import { type CustomTheme } from '../../provider/ThemeProvider';
import { checkUndefined, checkUndefinedNullOrEmpty, checkUndefinedOrNull } from '../Tools';
import InputNonEditValue from './helpers/render-non-edit-value';

export type InputCheckProps = {
    autoWidth?: boolean;
    blink?: boolean;
    callback?: (params: { id: string; val: boolean }) => void;
    className?: string;
    disabled?: boolean;
    edit?: boolean;
    endIcon?: IconifyProps;
    error?: string;
    fatLabel?: boolean;
    helperText?: string;
    hideLabel?: boolean;
    icon?: IconifyProps;
    id?: string;
    info?: string;
    l?: ReactNode;
    loading?: boolean;
    onClick?: () => void;
    testid?: string;
    textAlign?: 'left' | 'center' | 'right' | 'justify';
    type?: 'check' | 'switch';
    v?: boolean;
    vNew?: boolean;
};

export function convertCheckValue(v: InputCheckProps['v']): string {
    const val = !checkUndefinedNullOrEmpty(v) ? (v === true ? 'Ja' : 'Nein') : '-';
    return val;
}

const InputCheck = (moduleProps: InputCheckProps) => {
    const { autoWidth, blink, callback, disabled, edit, endIcon, error, fatLabel, helperText, hideLabel, icon, id, info, l, loading, onClick, testid, textAlign, type, v, vNew }: InputCheckProps = {
        type: 'check',
        ...moduleProps,
    };
    const [value, setValue] = useState<InputCheckProps['v']>(v);
    const theme: CustomTheme = useTheme();
    const containerRef = useRef<HTMLDivElement>(null);

    const handleSubmit = (val: InputCheckProps['v']) => {
        if (callback) callback({ id: id, val: val });
        else console.error('Callback-Funktion fehlt!\nInput-Key: inputCheck_' + id);
    };

    const handleChange = (e, val) => {
        const newVal = !checkUndefinedOrNull(val) ? val : '';
        setValue(newVal);
        handleSubmit(newVal);
    };

    const heading = [];
    if (!hideLabel) {
        if (icon) {
            heading.push(
                <span key={'inputCheck_' + id + '_headingIcon'} className="materialIcon iconMarginRight">
                    <Iconify icon={icon} />
                </span>
            );
        }
        if (Array.isArray(l)) heading.push(l[value === true ? 1 : 0]);
        else if (l) heading.push(l);
    }

    useEffect(
        function () {
            setValue(!checkUndefined(vNew) ? vNew : v);
        },
        [v, vNew]
    );

    useEffect(
        function () {
            if (blink) {
                setTimeout(() => {
                    containerRef?.current?.scrollIntoView({ block: 'center' });
                }, 500);
            }
        },
        [blink]
    );

    return edit ? (
        <div
            ref={containerRef}
            className={[blink ? 'blinkInputfield' : '', 'inputCheck'].join(' ')}
            style={{ display: 'flex', alignItems: 'center', width: !autoWidth ? '100%' : undefined }}
            key={'inputCheck_' + id}
        >
            {Array.isArray(l) && (
                <span
                    className="materialIcon"
                    style={{
                        color: !loading && !checkUndefinedOrNull(error) ? theme?.palette?.error?.main : undefined,
                        opacity: loading ? 0.5 : 1,
                        marginRight: 15,
                        marginBottom: !checkUndefinedOrNull(error) || !checkUndefinedOrNull(helperText) ? 12 : undefined,
                        whiteSpace: 'nowrap',
                    }}
                >
                    {Array.isArray(l) ? l[0] : l}
                </span>
            )}
            <FormControl onClick={onClick} disabled={loading || disabled} error={!checkUndefinedOrNull(error)}>
                <FormControlLabel
                    disabled={loading || disabled}
                    control={
                        <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {loading ? (
                                <Box sx={{ p: '9px' }}>
                                    <CircularProgress color="primary" size="1em" />
                                </Box>
                            ) : type === 'switch' ? (
                                <Switch
                                    disabled={loading || disabled}
                                    checked={!checkUndefined(value) ? value : null}
                                    color="primary"
                                    id={id}
                                    onClick={onClick}
                                    onChange={onClick ? undefined : handleChange}
                                />
                            ) : (
                                <Checkbox
                                    disabled={loading || disabled}
                                    checked={!checkUndefined(value) ? value : null}
                                    color="primary"
                                    id={id}
                                    onClick={onClick}
                                    onChange={onClick ? undefined : handleChange}
                                />
                            )}
                        </div>
                    }
                    id={id}
                    label={
                        <span style={{ display: 'flex', alignItems: 'center' }}>
                            {icon && (
                                <span className="materialIcon iconMarginRight">
                                    <Iconify icon={icon} />
                                </span>
                            )}
                            {Array.isArray(l) ? l[1] : l}
                            {!checkUndefinedOrNull(endIcon) && (
                                <div
                                    className="materialIcon"
                                    style={{ display: 'flex', alignItems: 'center', color: !loading && !checkUndefinedOrNull(error) ? theme?.palette?.error?.main : undefined, marginLeft: 8 }}
                                >
                                    <Iconify icon={endIcon} />
                                </div>
                            )}
                            {!checkUndefinedOrNull(info) && (
                                <div className="materialIcon" style={{ color: !loading && !checkUndefinedOrNull(error) ? theme?.palette?.error?.main : undefined, marginLeft: 5 }}>
                                    <Tooltip title={info}>
                                        <span>
                                            <Info color="primary" />
                                        </span>
                                    </Tooltip>
                                </div>
                            )}
                        </span>
                    }
                    labelPlacement={textAlign === 'right' ? 'start' : 'end'}
                    style={{ color: !loading && !checkUndefinedOrNull(error) ? theme?.palette?.error?.main : undefined }}
                />
                {!checkUndefinedOrNull(error) || !checkUndefinedOrNull(helperText) ? (
                    <FormHelperText style={{ marginTop: -5, marginLeft: type === 'switch' ? (Array.isArray(l) ? -35 : 46) : 27 }}>{!checkUndefinedOrNull(error) ? error : helperText}</FormHelperText>
                ) : undefined}
            </FormControl>
        </div>
    ) : (
        <InputNonEditValue
            autoWidth={autoWidth}
            endIcon={endIcon}
            fatLabel={fatLabel}
            finalId={!checkUndefinedNullOrEmpty(testid) ? testid : id}
            hideLabel={hideLabel}
            icon={icon}
            info={info}
            l={Array.isArray(l) ? l[value === true ? 1 : 0] : l}
            loading={loading}
            textAlign={textAlign}
            value={convertCheckValue(Array.isArray(l) ? true : v)}
        />
    );
};

export default InputCheck;
